import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import {
  createIdentityVerificationLinkToken,
  addPlaidIdentityVerification,
  addIdentityMatch,
  exchangePublicToken,
  getGUIDValidation,
  getOrganizationSettings,
  getOrganizationSettingsSection,
  getOrganizationSettingsName,
  createLinkTokenForUpdateItem,
} from 'services/api';
import {
  addPlaidLinkCallbackOnSuccess,
  addPlaidLinkCallbackOnEvent,
  addPlaidLinkCallbackOnExit,
} from 'modules/helpers';
import BrokersafeLogo from 'assets/icons/brokersafe_logo.svg';
import Button from 'components/Button';
import { Root } from './styles';
import Verification from './Verification';
import PaymentPreference from './PaymentPreference';
import { Customer, OrganizationSetting } from './types';

const Plaid = () => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [identityVerificationLinkToken, setIdentityVerificationLinkToken] =
    useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer>();
  const [idVerificationComplete, setIDVerificationComplete] = useState(false);
  const [isIDVSubmitting, setIDVSubmitting] = useState(false);
  const [loadingGUIDValidate, setLoadingGUIDValidated] = useState(true);
  const [searchParams] = useSearchParams();
  const [isVerified, setVerified] = useState(false);
  const [isPaymentPreferenceComplete, setPaymentPreferenceComplete] =
    useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isComplete, setComplete] = useState(false);
  const [isDigital, setIsDigital] = useState(false);
  const [settingSection, setSettingSection] = useState('');
  const [termsPageSettings, setTermsPageSettings] = useState<
    OrganizationSetting[]
  >([]);
  const [completePageSettings, setCompletePageSettings] = useState<
    OrganizationSetting[]
  >([]);
  const idvPk = useRef<number | undefined>();
  const [iDVResult, setIDVResult] = useState({} as OrganizationSetting);
  const [idvCompleteSettings, setIDVCompleteSettings] = useState<
    OrganizationSetting[]
  >([]);

  const validateGUID = useCallback(async () => {
    const guid = searchParams.get('guid');
    if (!guid) {
      setLoadingGUIDValidated(false);
      return;
    }

    try {
      const guidValidationRes = await getGUIDValidation(guid);
      if (guidValidationRes.GUID) {
        setCustomer(guidValidationRes);

        if (guidValidationRes.Plaid_Identity_Verification_Template_ID) {
          const idvLinkTokenRes = await createIdentityVerificationLinkToken(
            guidValidationRes,
          );
          // @ts-ignore
          setIdentityVerificationLinkToken(idvLinkTokenRes.linkToken);
        }

        const linkTokenRes = await createLinkTokenForUpdateItem(
          guidValidationRes,
        );
        // @ts-ignore
        setLinkToken(linkTokenRes.linkToken);

        const termsPageSettingsRes = await getOrganizationSettings(
          guidValidationRes.Organization_PK,
          'Contact Bank Connect',
          'Start - Micro-Deposit',
        );
        setTermsPageSettings(termsPageSettingsRes);

        const idvCompleteSettingsRes = await getOrganizationSettings(
          guidValidationRes.Organization_PK,
          'Contact IDV',
          'Completed',
        );
        setIDVCompleteSettings(idvCompleteSettingsRes);

        const completeSettingsSectionRes = await getOrganizationSettingsSection(
          guidValidationRes.Customer_PK,
        );
        setSettingSection(completeSettingsSectionRes.Settings_Section);

        const completePageSettingsRes = await getOrganizationSettings(
          guidValidationRes.Organization_PK,
          'Contact Bank Connect',
          completeSettingsSectionRes.Settings_Section,
        );
        setCompletePageSettings(completePageSettingsRes);
      }
      setLoadingGUIDValidated(false);
    } catch (error) {
      setLoadingGUIDValidated(false);
    }
  }, [searchParams]);

  useEffect(() => {
    validateGUID();
  }, [validateGUID]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      const escrowAccountFlag = searchParams.get('eaf');

      try {
        const tokenRes = await exchangePublicToken({
          public_token,
          metadata,
          customer,
          escrowAccountFlag,
        });
        if (idvPk.current) {
          addIdentityMatch(tokenRes.plaidAccessToken, idvPk.current);
        }
        setComplete(true);
        setSubmitting(false);
        addPlaidLinkCallbackOnSuccess(customer, metadata);
      } catch (error) {
        setSubmitting(false);
      }
    },
    onExit: (error, metadata: any) => {
      setSubmitting(false);
      addPlaidLinkCallbackOnExit(customer, error, metadata);
    },
    onEvent: (eventName, metadata: any) => {
      if (eventName === 'OPEN' || eventName === 'SELECT_INSTITUTION') {
        setSubmitting(true);
      }
      addPlaidLinkCallbackOnEvent(customer, metadata);
    },
  });

  const { open: openIDV, ready: readyIDV } = usePlaidLink({
    token: identityVerificationLinkToken,
    onSuccess: async (_, metadata) => {
      try {
        const resIdvData = await addPlaidIdentityVerification({
          customer,
          identity_verification_id: metadata.link_session_id,
        });
        const resOrgSettingName = await getOrganizationSettingsName(
          customer?.Organization_PK!,
          resIdvData.status === 'success'
            ? 'IDV Completed - Success'
            : 'IDV Completed - Fail',
        );
        setIDVResult(resOrgSettingName);
        idvPk.current = resIdvData.Plaid_Identity_Verification_PK;
        setIDVerificationComplete(true);
        setIDVSubmitting(false);
      } catch (error) {
        setIDVSubmitting(false);
      }
    },
    onExit: () => {
      console.log('onexit');
      setIDVSubmitting(false);
    },
  });

  const termsPageHeaderText = useMemo(() => {
    let text = '';
    const headerSetting = termsPageSettings.find(
      (el) => el.Settings_Name === 'Bank Connect - Micro-Deposit Verify Button',
    );
    if (headerSetting) {
      text = headerSetting.Settings_String || '';
    }

    return text;
  }, [termsPageSettings]);

  const completePageHeaderText = useMemo(() => {
    let text = '';
    let headerSetting;
    if (settingSection === 'Completed - Manual') {
      [headerSetting] = completePageSettings;
    } else {
      headerSetting = completePageSettings.find(
        (el) =>
          el.Settings_Name ===
          (isDigital
            ? 'Bank Connect - Completed'
            : 'Bank Connect - Completed (Check)'),
      );
    }

    if (headerSetting) {
      text = headerSetting.Settings_String || '';
    }
    return text;
  }, [completePageSettings, isDigital, settingSection]);

  const renderBody = () => {
    if (!customer) {
      return <p>We're sorry but the ID provided is not valid.</p>;
    }

    if (!isVerified) {
      return (
        <Verification
          onComplete={() => {
            setVerified(true);
            if (
              customer.Plaid_Identity_Verification_Template_ID &&
              !idVerificationComplete &&
              readyIDV
            ) {
              setIDVSubmitting(true);
              openIDV();
            }
          }}
          customer={customer}
        />
      );
    }

    if (
      customer.Plaid_Identity_Verification_Template_ID &&
      !idVerificationComplete &&
      readyIDV
    ) {
      return (
        <Spin
          style={{ width: '100%', marginTop: 150 }}
          tip="One moment while we complete the identity verification process."
          size="large"
          spinning={isIDVSubmitting}
        />
      );
    }

    const failSetting = idvCompleteSettings?.find(
      (setting) => setting.Settings_Name === 'IDV Completed - Fail',
    );
    if (
      iDVResult?.Settings_Name === 'IDV Completed - Fail' &&
      failSetting?.Settings_IDV_Stop_On_Fail_Flag
    ) {
      return <p>{iDVResult.Settings_String}</p>;
    }

    if (!isPaymentPreferenceComplete && customer) {
      return (
        <PaymentPreference
          onComplete={(isDigitalComplete: boolean) => {
            setPaymentPreferenceComplete(true);
            setComplete(!isDigitalComplete);
            setIsDigital(isDigitalComplete);
          }}
          customer={customer}
        />
      );
    }

    if (isComplete) {
      return (
        <div className="last-section">
          <p>{completePageHeaderText}</p>
        </div>
      );
    }

    return (
      <Spin
        style={{ width: '100%' }}
        tip="One moment while we complete the bank connect process."
        size="large"
        spinning={isSubmitting}
      >
        {!isSubmitting && (
          <div className="last-section">
            <p>{termsPageHeaderText}</p>
            <Button
              onClick={() => {
                open();
              }}
              disabled={!ready || !customer}
            >
              Verify Micro-Deposit
            </Button>
          </div>
        )}
      </Spin>
    );
  };

  if (loadingGUIDValidate) {
    return null;
  }

  return (
    <Root>
      <header className="header">
        {customer?.Organization_Logo_URL && (
          <img src={customer?.Organization_Logo_URL} alt="brokersafe-logo" />
        )}
      </header>
      <div className="container">{renderBody()}</div>
      <footer>
        <div className="tifle-logo-wrapper">
          <div>Powered By</div>
          <img src={BrokersafeLogo} alt="brokersafe-logo" />
        </div>
      </footer>
    </Root>
  );
};

export default Plaid;
